/**
 * analytics.ts — anonymous, demo-scoped instrumentation (Scope A8).
 *
 * Records only WHAT happened in the demo (which tour, reached BLS, completed the
 * add-client flow, used the client-view toggle, CTA clicks) — never any PII and
 * never anything on the PHI path (there is no PHI here). Events buffer in memory
 * and log to the console for dev visibility.
 *
 * A real sink is intentionally NOT chosen yet (Jake's call — self-hosted beacon vs.
 * a privacy-light third party). If `window.__DEMO_ANALYTICS_ENDPOINT__` is set at
 * runtime, events are sent via sendBeacon; otherwise NOTHING leaves the browser.
 */

export type DemoEventName =
  | "tour_started"
  | "tour_completed"
  | "reached_bls"
  | "perspective_toggled"
  | "add_client_completed"
  | "video_started"
  | "cta_clicked"
  | "soundboard_sample_played"
  | "script_created"
  | "script_printed";

interface DemoEvent {
  name: DemoEventName;
  props?: Record<string, string | number | boolean>;
  ts: number;
}

const buffer: DemoEvent[] = [];

export function track(name: DemoEventName, props?: Record<string, string | number | boolean>): void {
  // performance.now() avoids Date and is monotonic; good enough for ordering.
  const evt: DemoEvent = { name, props, ts: Math.round(typeof performance !== "undefined" ? performance.now() : 0) };
  buffer.push(evt);
  if (typeof console !== "undefined") console.debug("[demo-analytics]", name, props ?? {});

  if (typeof window !== "undefined") {
    const endpoint = (window as unknown as { __DEMO_ANALYTICS_ENDPOINT__?: string }).__DEMO_ANALYTICS_ENDPOINT__;
    if (endpoint && typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(endpoint, JSON.stringify(evt));
      } catch {
        /* analytics must never break the demo */
      }
    }
  }
}

/** Exposed for a debug readout / the demo's own QA. */
export function getDemoEvents(): DemoEvent[] {
  return buffer.slice();
}
